import { Balances } from '@/hooks/useBalances';
import { LINKS } from '@/config/contracts';

interface Props {
  balances: Balances;
  loading: boolean;
}

export function BalanceCards({ balances, loading }: Props) {
  return (
    <section>
      <h2 className="section-label">Your Balances</h2>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* vbUSDC */}
        <TokenCard
          name="vbUSDC"
          subtitle="Bridged USDC on Ternoa"
          balance={balances.vbUsdc}
          usd={balances.vbUsdc}
          decimals={2}
          borderColor="border-l-usdc"
          iconBg="bg-usdc-light"
          iconColor="text-usdc"
          iconSymbol="$"
          loading={loading}
          actions={[
            { label: 'Deposit', href: LINKS.bridgeUSDC, variant: 'secondary' },
          ]}
        />

        {/* vbUSDT */}
        <TokenCard
          name="vbUSDT"
          subtitle="Bridged USDT on Ternoa"
          balance={balances.vbUsdt}
          usd={balances.vbUsdt}
          decimals={2}
          borderColor="border-l-usdt"
          iconBg="bg-usdt-light"
          iconColor="text-usdt"
          iconSymbol="₮"
          loading={loading}
          actions={[
            { label: 'Deposit', href: LINKS.bridgeUSDT, variant: 'secondary' },
          ]}
        />

        {/* vbETH */}
        <TokenCard
          name="vbETH"
          subtitle="Bridged ETH on Ternoa"
          balance={balances.vbEth}
          usd={null}
          decimals={6}
          borderColor="border-l-eth"
          iconBg="bg-eth-light"
          iconColor="text-eth"
          iconSymbol="Ξ"
          loading={loading}
          actions={[
            { label: 'Deposit', href: LINKS.bridgeETH, variant: 'secondary' },
          ]}
        />

        {/* $CAPS + $KAT stacked vertically */}
        <div className="flex flex-col gap-4 sm:gap-6">
          <TokenCardCompact
            name="$CAPS"
            subtitle="Powers TIP & CIFER"
            balance={balances.caps}
            usd={null}
            decimals={0}
            borderColor="border-l-caps"
            iconBg="bg-caps-light"
            iconColor="text-caps"
            iconSymbol="C"
            loading={loading}
            actions={[
              { label: 'Deposit', href: LINKS.bridgeCAPS, variant: 'secondary' },
              { label: 'Buy', href: LINKS.buyCAPS, variant: 'primary' },
            ]}
          />
          <TokenCardCompact
            name="$KAT"
            subtitle="KAT Airdrop Token"
            balance={balances.kat}
            usd={null}
            decimals={1}
            borderColor="border-l-kat"
            iconBg="bg-kat-light"
            iconColor="text-kat"
            iconSymbol="K"
            loading={loading}
            actions={[]}
          />
        </div>
      </div>
    </section>
  );
}

interface TokenCardProps {
  name: string;
  subtitle: string;
  balance: number;
  usd: number | null;
  decimals: number;
  borderColor: string;
  iconBg: string;
  iconColor: string;
  iconSymbol: string;
  badge?: string;
  loading: boolean;
  actions: Array<{ label: string; href: string; variant: 'primary' | 'secondary' }>;
}

function TokenCard({
  name,
  subtitle,
  balance,
  usd,
  decimals,
  borderColor,
  iconBg,
  iconColor,
  iconSymbol,
  badge,
  loading,
  actions,
}: TokenCardProps) {
  const formatted = loading
    ? '...'
    : balance.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return (
    <div className={`glass-card p-5 sm:p-8 border-l-[3px] ${borderColor} flex flex-col`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div
          className={`w-9 h-9 sm:w-11 sm:h-11 rounded-ternoa flex items-center justify-center text-base sm:text-lg font-bold ${iconBg} ${iconColor}`}
        >
          {iconSymbol}
        </div>
        <div>
          <div className="font-display text-base sm:text-xl font-semibold tracking-tight">{name}</div>
          <div className="text-xs sm:text-sm text-muted">{subtitle}</div>
        </div>
      </div>

      {/* Balance */}
      <div className="font-display text-2xl sm:text-4xl font-bold tracking-tight leading-tight">{formatted}</div>
      {usd !== null && (
        <div className="text-sm text-muted mt-1">
          ≈ ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}

      {/* Badge */}
      {badge && (
        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-success bg-success/10 rounded-ternoa px-2.5 py-1 mt-3">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
          {badge}
        </div>
      )}

      {/* Spacer pushes actions to bottom */}
      <div className="flex-1" />

      {/* Actions */}
      <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
        {actions.map((action) => (
          <a
            key={action.label}
            href={action.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 text-center ${action.variant === 'primary' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
          >
            {action.variant === 'secondary' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            )}
            {action.variant === 'primary' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            )}
            {action.label}
          </a>
        ))}
      </div>
    </div>
  );
}

/** Compact card for the stacked CAPS/KAT column */
function TokenCardCompact({
  name,
  subtitle,
  balance,
  usd,
  decimals,
  borderColor,
  iconBg,
  iconColor,
  iconSymbol,
  loading,
  actions,
}: TokenCardProps) {
  const formatted = loading
    ? '...'
    : balance.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return (
    <div className={`glass-card p-4 sm:p-5 border-l-[3px] ${borderColor} flex flex-col flex-1`}>
      {/* Header row — icon, name, balance all inline */}
      <div className="flex items-center gap-2.5 mb-2">
        <div
          className={`w-8 h-8 rounded-ternoa flex items-center justify-center text-sm font-bold ${iconBg} ${iconColor}`}
        >
          {iconSymbol}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold tracking-tight">{name}</div>
          <div className="text-[11px] text-muted truncate">{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-lg sm:text-xl font-bold tracking-tight leading-tight">{formatted}</div>
          {usd !== null && (
            <div className="text-[11px] text-muted">
              ≈ ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {actions.length > 0 && (
        <div className="flex gap-2 mt-auto pt-2">
          {actions.map((action) => (
            <a
              key={action.label}
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 text-center ${action.variant === 'primary' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              style={{ height: '32px', fontSize: '11px' }}
            >
              {action.variant === 'secondary' && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              )}
              {action.variant === 'primary' && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              )}
              {action.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
